import {
  DimensionSet,
  MetricProject,
  OrgBillingPartyMetric,
  OrgBillingPartyRecipientCountryMetric,
  OrgMetric,
  OrgProjectGlobalSmsExportMetric,
  OrgProjectJourneyMetric,
  OrgProjectMetric,
  OrgProjectProviderBillingPartyMetric,
  OrgProjectWhatsAppTemplateCategoryRecipientCountryMetric,
  OrgProviderBillingPartyMetric,
  OrgWhatsAppTemplateCategoryMetric,
  UsageLimit,
} from 'modules/domains/billing';
import { ApiMethod, getRaw, postRaw } from 'services/api';
import {
  RestQueryHookOptions,
  useRestMutation,
  UseRestMutationOptions,
  useRestQuery,
} from 'utils/hooks/api';

export const ZUORA_CREDENTIALS_URL = '/payments/getZuoraCredentials';
export const RECENT_PAYMENT_UPDATE_URL = '/payments/makeRecentPaymentMethodDefault';
export const TRIAL_END_DATE_URL = '/payments/getTrialEndDate';
export const GET_CREDIT_CARDS_URL = '/payments/zuoraCreditCards/';
export const SEND_ZUORA_USAGE_URL = '/i/subscriptions/sendUsage';
export const INSERT_ZUORA_PLAN_URL = '/i/subscriptions';
export const UPDATE_TRIAL_END_DATE_URL = '/payments/updateTrialEndDate';
export const ACCOUNT_USAGE_URL = '/i/account/usage';
export const ACCOUNT_USAGE_V2_URL = '/i/account/usageV2';
export const ACCOUNT_USAGE_V4_URL = '/i/account/usageV4';
export const BILLING_PERIODS_URL = '/i/account/billingPeriods';
export const ZUORA_PRODUCTS_URL = '/payments/zuoraProducts';
export const ZUORA_RATEPLANS_URL = '/payments/zuoraRatePlans';
export const CREATE_ZUORA_OBJECT_URL = '/payments/createZuoraObject';
export const UPDATE_ZUORA_OBJECT_URL = '/payments/updateZuoraObject';
export const DELETE_ZUORA_OBJECT_URL = '/payments/deleteZuoraObject';

/** Account Usage */
export type UseAccountUsageParams = {
  /**
   * comma separated list of metric aggregation levels for the requested metric groups
   *
   * There are no defaults or catch-all aggregation levels.
   * At least one aggregation level is required.
   * Aggregation level names are case-sensitive and must be one of the valid [DIMENSION_SET].
   * All requested aggregation levels must be explicitly specified
   *
   * Example:
   * [DimensionSet.ByOrg, DimensionSet.ByOrgProject].join(',')
   */
  aggLevels: string;
  /**
   * (inclusive) end of the date range for aggregated metrics (in PST timezone).
   *
   * There is no support for partial day results, thus data for the day same as the day of
   * the request is not available. The largest valid date is the day, before the request day.
   *
   * endDatePST must be larger or equal to startDatePST
   * Example: 2025-06-07
   */
  endDatePST: string;
  /**
   * optional comma separated list of enabled per-request FeatureFlags
   *
   * This is intended for troubleshooting purposes only.
   * This parameter is ignored unless BillingPerRequestFlagsAllowed FF is temporarily set for the org
   * for troubleshooting purposes.
   *
   * BillingPerRequestFlagsAllowed is never allowed to be passed via `flags`
   * There is no guarantee a particular FF is always supported.
   * The list of available flags depends on individual commit.
   *
   * Examples: "BillingJvtReporting,BillingWhatsAppReporting" !!! TBD
   */
  flags?: string;
  /**
   * comma separated list of requested metrics groups.
   *
   * There are no defaults or catch-all group names.
   * At least one group is required.
   * Group names are case-sensitive and must be one of the valid [[UsageMetricsGroup]].
   * All requested groups must be explicitly specified
   *
   * Example: [UsageMetricGroups.Catalog, UsageMetricGroups.Messaging].join(',')
   */
  metricGroups: string;
  /**
   * (inclusive) start of the date range for aggregated metrics (in PST timezone).
   *
   * If usage data is not available yet for this date (i.e. the org has been created at a later date),
   * the first valid date is used instead.
   *
   * The effective startDatePST is returned in the response
   * Example: 2025-06-01
   */
  startDatePST: string;
};

export type UseAccountUsageResponse = {
  lastAvailableDatePST: string;
  /** optional object returned when project-level aggregation level is used */
  projects?: Record<string, MetricProject>;
  /** grouped list of metric values based on aggregation level */
  values: {
    /** org-level metrics */
    [DimensionSet.ByOrg]?: { 0: OrgMetric[] };
    /** SMS Billing party metrics */
    [DimensionSet.ByOrgBillingParty]?: { 0: OrgBillingPartyMetric[] };
    /** Global SMS Billing party metrics */
    [DimensionSet.ByOrgBillingPartyRecipientCountry]?: {
      0: OrgBillingPartyRecipientCountryMetric[];
    };
    [DimensionSet.ByOrgProviderBillingParty]?: {
      0: OrgProviderBillingPartyMetric[];
    };
    [DimensionSet.ByOrgWhatsAppTemplateCategory]?: {
      0: OrgWhatsAppTemplateCategoryMetric[];
    };
    /** project-level metrics */
    [DimensionSet.ByOrgProject]?: {
      [orgProjectId: string]: OrgProjectMetric[];
    };
    [DimensionSet.ByOrgProjectGlobalSmsExport]?: {
      [orgProjectId: string]: OrgProjectGlobalSmsExportMetric[];
    };
    [DimensionSet.ByOrgProjectJourney]?: {
      [orgProjectId: string]: OrgProjectJourneyMetric[];
    };
    [DimensionSet.ByOrgProjectProviderBillingParty]?: {
      [orgProjectId: string]: OrgProjectProviderBillingPartyMetric[];
    };
    [DimensionSet.ByOrgProjectWhatsAppTemplateCategoryRecipientCountry]?: {
      [orgProjectId: string]: OrgProjectWhatsAppTemplateCategoryRecipientCountryMetric[];
    };
  };
};

export const useAccountUsage = (
  params: UseAccountUsageParams,
  options?: RestQueryHookOptions<UseAccountUsageResponse>,
) =>
  useRestQuery<UseAccountUsageResponse, UseAccountUsageParams>(
    ACCOUNT_USAGE_V4_URL,
    params,
    options,
  );

/** Billing Usage Limits */
export const BILLING_USAGE_LIMITS_URL = '/i/account/usageLimits';

export type BillingUsageLimitsResponse = {
  maxTermEndDatePST: string;
  metricUsageLimit: Record<string, UsageLimit[]>;
  minTermStartDatePST: string;
};

export type BillingUsageLimitsBody = {
  metricNames: string[];
};

export const useBillingUsageLimits = (options?: UseRestMutationOptions) => {
  return useRestMutation<BillingUsageLimitsResponse, BillingUsageLimitsBody>(
    BILLING_USAGE_LIMITS_URL,
    ApiMethod.post,
    options,
  );
};

export enum BillingPeriodType {
  Current = 'Current',
  HistoricalCurrentTerm = 'HistoricalCurrentTerm',
}

/** The last returned billing period includes current date (on the moment of calling the endpoint) */
export type BaseBillingPeriod = {
  /** endDatePST is always greater than startDatePST */
  endDatePST: string;
  /**
   * First billing period startDatePST matches  minimaltermStartDatePST across
   * all defined metrics (for the current org)
   */
  startDatePST: string;
  type: BillingPeriodType;
};

export type UseBillingPeriodsResponse = {
  billingPeriods: BaseBillingPeriod[];
};

/** Billing Periods */
export const useBillingPeriods = (options?: RestQueryHookOptions<UseBillingPeriodsResponse>) => {
  return useRestQuery<UseBillingPeriodsResponse>(BILLING_PERIODS_URL, undefined, options);
};

export function getZuoraCredentials() {
  return getRaw(ZUORA_CREDENTIALS_URL);
}

export function makeRecentPaymentMethodDefault(zuoraAccountName: string) {
  return postRaw(RECENT_PAYMENT_UPDATE_URL, { zuoraAccountName });
}

export function getTrialEndDate() {
  return getRaw(TRIAL_END_DATE_URL);
}

export function getCreditCardsOnFile(zuoraAccountId: string) {
  return getRaw(`${GET_CREDIT_CARDS_URL}${zuoraAccountId}`);
}

export function sendZuoraUsage(billRunDateTime: string, orgId?: number) {
  return postRaw(SEND_ZUORA_USAGE_URL, { orgId, billRunDateTime });
}

export function insertZuoraPaymentPlan(zuoraAccount: unknown) {
  return postRaw(INSERT_ZUORA_PLAN_URL, zuoraAccount);
}

type TrialParams = {
  orgId: number | string;
  billRunDate: string;
};
export function updateTrialEndDate(trialEndDateValue: TrialParams) {
  return postRaw(UPDATE_TRIAL_END_DATE_URL, { ...trialEndDateValue });
}

/**
 * getZuoraProducts
 * gets an Object that contains a list of all the Zuora Products
 * @return { Object } Object containing a list of Zuora Product Objects
 */
export function getZuoraProducts() {
  return getRaw(`${ZUORA_PRODUCTS_URL}`);
}

/**
 * getZuoraRatePlans
 * gets an Object that contains a list of all the Zuora Rateplans
 * @param { Object } ratePlanInfo: contains productId, pageSize, pageNum, getTotal
 * @return { Object } Object containing a list of Zuora RatePlanse
 */
type RatePlanInfo = {
  productId: string | number;
  pageSize: string | number;
  pageNum: string | number;
  getTotal: boolean;
};
export function getZuoraRatePlans({ productId, pageSize, pageNum, getTotal }: RatePlanInfo) {
  return getRaw(
    `${ZUORA_RATEPLANS_URL}?productId=${productId}&pageSize=${pageSize}&pageNum=${pageNum}&getTotal=${getTotal}`,
  );
}

/**
 * createZuoraObjectCall
 * creates a zuora object by sending the call to zuoras api
 * @param { Object } objectInfo: contains object information
 * @param { String } objectType: string with Object Type
 * @return { Object } Object containing if process was a success and object ids affected
 */
export function createZuoraObjectCall(objectInfo: unknown, objectType: unknown) {
  return postRaw(CREATE_ZUORA_OBJECT_URL, { objectInfo, objectType });
}

/**
 * updateZuoraObjectCall
 * updates a zuora object by sending the call to zuoras api
 * @param { Object } objectInfo: contains object information
 * @param { String } objectType: string with Object Type
 * @return { Object } Object containing if process was a success and object ids affected
 */
export function updateZuoraObjectCall(objectInfo: unknown, objectType: unknown) {
  return postRaw(UPDATE_ZUORA_OBJECT_URL, { objectInfo, objectType });
}

/**
 * deleteZuoraObjectCall
 * deletes a zuora object by sending the call to zuoras api
 * @param { String } objectId: string with id of object to delete
 * @param { String } objectType: string with Object Type
 * @return { Object } Object containing if process was a success and object ids affected
 */
export function deleteZuoraObjectCall(objectId: string | number, objectType: unknown) {
  return postRaw(DELETE_ZUORA_OBJECT_URL, { objectId, objectType });
}
